// ts-rest contract for the country risk register (SEP-228 §35).
//
// Two doors. The read serves the current classification of every country, so a
// caller scores a country answer without a second call. The write records one
// new classification, and only Compliance and admin reach it.
//
// The register is append-only, so there is no update door and no delete door. A
// change of classification is a new row with a later effective day.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { countryFatfStatusSchema, countryRiskClassificationSchema } from '../country-risk';
import { errorBody } from '../schemas/common';

const c = initContract();

export const countryRiskRegisterResponseSchema = z.object({
    /** The current classification of every country the register holds. */
    classifications: z.array(countryRiskClassificationSchema),
    /**
     * The day Compliance last maintained the FATF grey list, or null when the
     * register holds no grey-list row at all. A null says the firm has not
     * loaded a FATF grey list yet, and every grey-list country therefore scores
     * 1 rather than 10. The FATF list is never empty in practice, so a null
     * never means "loaded and empty".
     *
     * A de-listing moves this day as an addition does. A plenary that only
     * REMOVES a country adds a row that is not grey, so the grey rows alone
     * would leave the day at the last addition and the register would look
     * stale.
     */
    fatfListAsOf: z.string().nullable(),
    /** The count of ISO countries with no row at all. */
    unclassifiedCount: z.number().int(),
});
export type CountryRiskRegisterResponse = z.infer<typeof countryRiskRegisterResponseSchema>;

export const setCountryRiskClassificationBodySchema = z.object({
    countryCode: z.string(),
    ukDomestic: z.boolean().optional(),
    fatfStatus: countryFatfStatusSchema,
    /** The day the classification takes effect. */
    effectiveFrom: z.string(),
    note: z.string().nullable().optional(),
});
export type SetCountryRiskClassificationBody = z.infer<
    typeof setCountryRiskClassificationBodySchema
>;

export const countryRiskContract = c.router(
    {
        register: {
            method: 'GET',
            path: '/country-risk',
            responses: { 200: countryRiskRegisterResponseSchema, 403: errorBody },
            summary: 'The current AML risk classification of every country on the register',
        },
        setClassification: {
            method: 'POST',
            path: '/country-risk',
            body: setCountryRiskClassificationBodySchema,
            responses: {
                200: z.object({ classification: countryRiskClassificationSchema }),
                400: errorBody,
                403: errorBody,
                409: errorBody,
            },
            summary: "Record a country's AML risk classification from a given day",
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
